/**
 * Session Expiry Handler
 *
 * Centralized handler for session expiration detection and user notification.
 * Prevents duplicate modals when multiple API calls fail simultaneously.
 *
 * Usage:
 *   import { showSessionExpiredModal, isSessionExpiredError } from './sessionExpiryHandler';
 *
 * Testing:
 *   In browser console (development mode only):
 *   window.testSessionExpiry() - Shows the session expired modal
 */

import { Modal } from 'antd';
import { msalInstance } from '../index';

// Global flag to prevent duplicate modals
let isModalShown = false;

/**
 * Show session expired modal and handle logout
 * Only shows one modal even if called multiple times
 */
export const showSessionExpiredModal = () => {
    // Prevent duplicate modals from concurrent API calls
    if (isModalShown) {
        console.log('[SessionExpiry] Modal already shown, preventing duplicate');
        return;
    }

    isModalShown = true;
    console.log('[SessionExpiry] Showing session expired modal');

    Modal.error({
        title: 'Session Expired',
        content: 'Your session has expired. Please log in again to continue.',
        centered: true,
        maskClosable: false,
        keyboard: false,
        okText: 'Return to Login',
        width: 500,
        onOk: async () => {
            await handleLogout();
        }
    });
};

/**
 * Handle logout and cleanup
 */
const handleLogout = async () => {
    try {
        console.log('[SessionExpiry] Clearing storage and logging out');
        localStorage.clear();
        sessionStorage.clear();

        await msalInstance.logoutRedirect({
            postLogoutRedirectUri: '/',
        });
    } catch (error) {
        console.error('[SessionExpiry] Error during logout:', error);
        // Force redirect as fallback
        window.location.href = '/';
    } finally {
        // CRITICAL FIX: Reset modal flag after logout completes
        // This ensures modal can be shown again if user logs back in and session expires
        isModalShown = false;
    }
};

/**
 * Check if error indicates MSAL session expiration
 *
 * @param {Error} error - The error to check
 * @returns {boolean} - True if this is a session expiry error
 */
export const isSessionExpiredError = (error) => {
    if (!error) return false;

    // Check for MSAL InteractionRequiredAuthError
    if (error.errorCode === 'interaction_required' ||
        error.errorCode === 'consent_required' ||
        error.errorCode === 'login_required' ||
        error.name === 'InteractionRequiredAuthError') {
        console.log('[SessionExpiry] Detected MSAL interaction required:', error.errorCode || error.name);
        return true;
    }

    // Check error message for B2C session expiry patterns
    const errorMessage = error.message || error.toString();
    if (errorMessage.includes('interaction_required') ||
        errorMessage.includes('AADB2C90077') || // B2C session expired
        errorMessage.includes('AADB2C90080')) { // B2C token expired
        console.log('[SessionExpiry] Detected session expiry from error message');
        return true;
    }

    return false;
};

/**
 * Development/Testing utility
 * Allows developers to test session expiry modal without waiting for actual B2C token expiration:
 * - SPA Refresh Token: 24 hours (Azure B2C default for SPAs using PKCE)
 * - Sliding Window: 90 days (default, configurable 1-365 days)
 * Note: MSAL automatically refreshes access tokens (60 min lifetime) using refresh tokens
 * Only available in development mode
 */
if (process.env.NODE_ENV === 'development') {
    window.testSessionExpiry = () => {
        console.log('[SessionExpiry] TEST: Triggering session expiry modal');
        isModalShown = false; // Reset flag to allow showing
        showSessionExpiredModal();
    };

    console.log('[SessionExpiry] Development mode: Use window.testSessionExpiry() to test the modal');
}